<template>
  <div
    :class="$style.paginator"
    data-sb-regression="sb-cursor-paginator"
  >
    <ButtonGroup>
      <SbButton
        variation="minimal"
        :disabled="!props.cursors.first"
        data-sb-automation="first-page"
        @click="fetchPage('first')"
      >
        <span :class="['pi', 'pi-angle-left', $style.firstPageIcon]" />
      </SbButton>
      <SbButton
        variation="minimal"
        :disabled="!props.cursors.previous"
        data-sb-automation="previous-page"
        @click="fetchPage('previous')"
      >
        <span :class="['pi', 'pi-angle-left']" />
        <SbText value="sbTable.previous" />
      </SbButton>
      <SbButton
        variation="minimal"
        :disabled="!props.cursors.next"
        data-sb-automation="next-page"
        @click="fetchPage('next')"
      >
        <SbText value="sbTable.next" />
        <span :class="['pi', 'pi-angle-right']" />
      </SbButton>
      <SbButton
        variation="minimal"
        :disabled="!props.cursors.last"
        data-sb-automation="last-page"
        @click="fetchPage('last')"
      >
        <span :class="['pi', 'pi-angle-right', $style.lastPageIcon]" />
      </SbButton>
    </ButtonGroup>
    <div :class="$style.pageSizeOptions">
      <label
        v-if="!!totalRecords"
        :class="$style.paginationLabel"
        data-sb-automation="total-records-label"
      >{{ $t(paginationText[0], paginationText[1]) }} | </label>
      <label
        :class="$style.paginationLabel"
        :data-sb-automation="page-size-label"
      >{{ $t('sbTable.pageSize') }}:</label>
      <SbSelect
        v-model="amountOfRecordsInPage"
        :options="props.pageSizeOptions"
        data-sb-automation="page-size-select"
        :pt:option="{
          'data-sb-automation': 'paginator-sb-option'
        }"
        :pt:overlay="{
          'data-sb-automation': 'paginator-overlay'
        }"
        :pt:label="{
          'data-sb-automation': 'paginator-label'
        }"
      />
    </div>
  </div>
</template>

<script setup>
import SbButton from '@src/components/Button/SbButtonNext.vue';
import SbSelect from '@src/components/Select/SbSelect.vue';
import SbText from '@src/components/Text/SbText.vue';
import ButtonGroup from 'primevue/buttongroup';
import { defineProps, defineEmits, computed } from 'vue';

const props = defineProps({
  pageSize: {
    type: Number,
    default: () => 5,
  },
  pageSizeOptions: {
    type: Array,
    default: () => [5, 15, 20, 25, 50, 100],
  },
  cursors: {
    type: Object,
    default: () => ({
      first: true,
      previous: true,
      next: true,
      last: true,
    }),
    required: true,
  },
  totalRecords: {
    type: Number,
    default: 0,
  },
  currentRecordCount: {
    type: Number,
    default: 5,
  },
  pageNumber: {
    type: Number,
    default: null,
  },
});
const emit = defineEmits(['fetch-page']);

const fetchPage = (value, type = 'page') => {
  emit('fetch-page', { type, value });
};

const firstIndex = computed(() => {
  return props.pageSize * (props.pageNumber - 1) + 1;
});

const lastIndex = computed(() => {
  if (props.pageSize === props.currentRecordCount) {
    return props.pageSize * props.pageNumber;
  }
  return firstIndex.value + props.currentRecordCount - 1;
});

const paginationText = computed(() => {
  if (props.pageNumber) {
    return [
      'sbTable.showTotalRecords',
      {
        firstIndex: firstIndex.value,
        lastIndex: lastIndex.value,
        totalRecords: props.totalRecords,
      },
    ];
  }

  return [
    'sbTable.showTotalRecordsLegacy',
    {
      pageSize: props.pageSize > props.totalRecords ? props.totalRecords : props.pageSize,
      totalRecords: props.totalRecords,
    },
  ];
});

const amountOfRecordsInPage = computed({
  get: () => props.pageSize,
  set: (value) => fetchPage(value, 'page-size'),
});
</script>

<style lang="scss" module>
.paginator {
	align-items: center;
	bottom: 0;
	display: flex;
  flex-flow: row wrap;
  gap: var(--p-spacing-xs);
  justify-content: space-between;
	position: sticky;

  :global(.pi) {
    line-height: .65;
  }

  :global(:not(:disabled).p-button-outlined:hover .pi) {
    border-color: white;
  }
}

.paginationLabel {
	color: #a6a6a6;
	margin-right: var(--p-spacing-xxs);
  margin-bottom: 0;
}

.pageSizeOptions {
	align-items: center;
	display: flex;
	justify-content: space-between;

  :global(.p-select-label) {
    padding: var(--p-select-padding-y) 0 var(--p-select-padding-y) var(--p-select-padding-x);
  }
}

.firstPageIcon {
  border-left: 1.96px solid var(--p-primary-color);
}

.lastPageIcon {
  border-right: 1.96px solid var(--p-primary-color);
}
</style>
